// The conversation controller: everything between the store and the API.
// No DOM in here. The renderer (widget.ts) subscribes to the store and
// calls back into this object for user actions.
//
// Consistency rule: GET /v1/conversations/:id is the only source of truth
// for the transcript. Every send, stream frame, tab focus and fallback
// tick ends in refresh(), which replaces conversation + messages wholesale;
// the renderer reconciles by message id so nothing flickers.

import { ApiError, isApiError, NetworkError, type Api } from './api'
import { isJwtStale } from './identity'
import { IdentityError, type SessionManager } from './session'
import { clearConversationId, readConversationId, readSession } from './storage'
import type { Store } from './store'
import type { AttachmentInput, Conversation, ConversationDetail, Message } from './types'

export interface ChatHooks {
  /** The conversation just went active → resolved. */
  onResolved(): void
  /** The JWT is stale or rejected; the host should call identify(). */
  onIdentityExpired(): void
}

export interface ChatDeps {
  api: Api
  session: SessionManager
  store: Store
  preview: boolean
  getJwt: () => string | undefined
  hooks: ChatHooks
}

export interface Chat {
  /** Restores a stored conversation on first open, or lands on welcome. */
  hydrate(): Promise<void>
  /** Resolves false when the message could not be sent; the caller restores it. */
  send(text: string, file?: File): Promise<boolean>
  refresh(): Promise<void>
  startNew(): void
  submitCsat(satisfied: boolean, comment?: string): Promise<void>
  openHistory(): Promise<void>
  openHistoryDetail(id: string): Promise<void>
  backToChat(): void
  /** Throws IdentityError when the server rejects the JWT. */
  identify(jwt: string | null): Promise<void>
  /** The panel was opened: make sure the transcript is fresh and streaming. */
  onOpen(): void
  /** The panel was closed: nothing to keep up to date until it reopens. */
  onClose(): void
  destroy(): void
}

const REPLY_TIMEOUT_MS = 30_000
const FALLBACK_MS = 15_000
const IDENTITY_WAIT_MS = 3_000

export const NOTICE_FRESH = 'Continuing a fresh conversation.'
export const NOTICE_SIGNED_IN =
  'Signed in. The chat you had before is still with our team; this one starts fresh.'
export const ERR_IDENTITY = 'Your session expired. Please sign in again.'
export const ERR_ORIGIN = "This site isn't allowed to use chat."
export const ERR_NETWORK = "Can't reach chat right now. Check your connection and try again."
export const ERR_GENERIC = 'Something went wrong. Please try again.'
// The site has used up its monthly conversations. Nothing the visitor did,
// and nothing they can fix — so say only that chat is closed for now.
export const ERR_QUOTA = "Chat isn't taking new conversations right now. Please try again later."
export const ERR_UPLOAD = "Couldn't upload the image. Please try again."
export const ERR_FILE_TYPE = 'Only images can be attached.'
export const ERR_FILE_SIZE = 'Images need to be under 10 MB.'

export const MAX_FILE_BYTES = 10 * 1024 * 1024
export function fileProblem(file: File): string | null {
  if (!file.type.startsWith('image/')) return ERR_FILE_TYPE
  if (file.size > MAX_FILE_BYTES) return ERR_FILE_SIZE
  return null
}

export function createChat(deps: ChatDeps): Chat {
  const { api, session, store, hooks } = deps
  const s = () => store.get()

  let destroyed = false
  let sse: EventSource | null = null
  let fallbackTimer: number | undefined
  let replyTimer: number | undefined
  let inflight: Promise<void> | null = null
  let dirty = false
  // Non-user message ids present when the last send went out; a reply is
  // any non-user message not in here.
  let replyBaseline = new Set<string>()
  let identityWaiter: { before: string | undefined; resolve: (jwt: string | null) => void } | null = null
  let previewCounter = 0

  // ── Transcript ──────────────────────────────────────────────────────

  function conversationId(): string | null {
    return s().conversation?.id ?? readConversationId()
  }

  function refresh(): Promise<void> {
    if (deps.preview || destroyed) return Promise.resolve()
    if (inflight) {
      dirty = true
      return inflight
    }
    inflight = (async () => {
      do {
        dirty = false
        const id = conversationId()
        if (!id) return
        try {
          const out = await session.authed((t) => api.getConversation(t, id))
          if (!out.ok) {
            lostConversation()
            return
          }
          apply(out.value)
        } catch (err) {
          if (isApiError(err, 404)) lostConversation()
          else if (!(err instanceof NetworkError)) fail(err)
          return
        }
      } while (dirty && !destroyed)
    })().finally(() => {
      inflight = null
    })
    return inflight
  }

  function apply({ conversation, messages }: ConversationDetail) {
    const prev = s()
    let pending = prev.pending
    if (pending) {
      const p = pending
      const echoed = p.serverId
        ? messages.some((m) => m.id === p.serverId)
        : messages.some((m) => m.role === 'user' && m.content === p.content)
      if (echoed) {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl)
        pending = null
      }
    }
    let awaitingReply = prev.awaitingReply
    if (awaitingReply) {
      const replied = messages.some((m) => m.role !== 'user' && !replyBaseline.has(m.id))
      if (replied || conversation.takeoverActive || conversation.status !== 'active') awaitingReply = false
    }
    if (!awaitingReply) clearReplyTimer()
    const phase = prev.phase === 'welcome' || prev.phase === 'loading' ? 'chat' : prev.phase
    store.set({
      conversation,
      messages,
      pending,
      awaitingReply,
      phase,
      csatSubmitted: prev.csatSubmitted || !!conversation.csat,
    })
    if (prev.conversation?.status !== 'resolved' && conversation.status === 'resolved') hooks.onResolved()
    // A closed conversation can be reopened by the team, and the visitor is
    // sitting right there: stop streaming (the API refuses it anyway) but keep
    // polling, so the composer comes back on its own.
    if (conversation.status === 'active') connect()
    else {
      closeStream()
      watch()
    }
  }

  // The stored conversation is gone or the session is dead: back to the
  // welcome state, with a line explaining why the thread looks new.
  function lostConversation(notice = NOTICE_FRESH) {
    disconnect()
    clearReplyTimer()
    clearConversationId()
    store.set({
      conversation: null,
      messages: [],
      pending: null,
      awaitingReply: false,
      csatSubmitted: false,
      phase: 'welcome',
      notice,
    })
  }

  async function hydrate() {
    if (deps.preview) return
    store.set({ verified: session.isVerified() })
    if (!readConversationId() || !readSession()) {
      store.set({ phase: 'welcome' })
      return
    }
    store.set({ phase: 'loading' })
    await session.refreshIfStale()
    await refresh()
    if (s().phase === 'loading') store.set({ phase: 'welcome' })
    else connect()
  }

  // ── Sending ─────────────────────────────────────────────────────────

  async function send(text: string, file?: File): Promise<boolean> {
    const st = s()
    if (destroyed || st.pending) return false
    if (st.conversation && st.conversation.status !== 'active') return false
    if (file) {
      const problem = fileProblem(file)
      if (problem) {
        store.set({ error: problem })
        return false
      }
    }
    const previewUrl = file ? URL.createObjectURL(file) : undefined
    const pending = () => ({ localId: `local-${Date.now()}`, content: text, previewUrl })
    store.set({ error: null, pending: pending(), phase: st.phase === 'welcome' ? 'chat' : st.phase })
    if (deps.preview) return previewSend(text)

    if (isJwtStale(deps.getJwt()) && !(await waitForIdentity())) return abort(ERR_IDENTITY)

    try {
      let id = conversationId()
      // A conversation has to exist before a file can be attached to it: the
      // text opens it, the image follows as its own message.
      let content = text
      if (!id && file) {
        await bootstrap(text || 'Sent an image', { keepPending: true })
        id = conversationId()
        content = ''
      }
      if (id) {
        try {
          const attachments = file ? [await upload(id, file)] : undefined
          const out = await session.authed((t) => api.sendMessage(t, id!, content, attachments))
          if (out.ok) {
            markSent(out.value.id)
            await refresh()
            return true
          }
        } catch (err) {
          // Gone, or closed by the team: either way the message starts a new one.
          if (!isApiError(err, 404) && !isApiError(err, 409, 'conversation_resolved')) throw err
        }
        if (file) throw new ApiError(409, 'conversation_resolved')
        // Session dead or conversation gone: carry the text into a new one.
        lostConversation()
        store.set({ pending: pending(), phase: 'chat' })
      }
      await bootstrap(text)
      return true
    } catch (err) {
      return abort(messageFor(err))
    }
  }

  // Presign, PUT the bytes, and describe the file for the message.
  async function upload(id: string, file: File): Promise<AttachmentInput> {
    let ticket
    try {
      ticket = await session.authed((t) => api.requestUpload(t, id, file.type, file.size))
      if (!ticket.ok) throw new ApiError(401, 'session_invalid')
      await api.putFile(ticket.value.uploadUrl, file, file.type)
    } catch (err) {
      if (isApiError(err, 401)) throw err
      throw new ApiError(0, 'upload_failed', err instanceof Error ? err.message : undefined)
    }
    return { key: ticket.value.key, mimeType: file.type, sizeBytes: file.size, originalFilename: file.name }
  }

  async function bootstrap(text: string, opts: { keepPending?: boolean } = {}) {
    try {
      await session.bootstrap(text, sourceUrl())
    } catch (err) {
      if (!(err instanceof IdentityError) || !(await waitForIdentity())) throw err
      await session.bootstrap(text, sourceUrl())
    }
    if (!opts.keepPending) markSent(undefined)
    store.set({ verified: session.isVerified(), phase: 'chat' })
    if (!opts.keepPending) await refresh()
    connect()
  }

  function markSent(serverId: string | undefined) {
    const st = s()
    replyBaseline = new Set(st.messages.filter((m) => m.role !== 'user').map((m) => m.id))
    const awaitingReply = !st.conversation?.takeoverActive
    store.set({
      pending: st.pending ? { ...st.pending, serverId } : null,
      awaitingReply,
    })
    clearReplyTimer()
    if (awaitingReply) {
      replyTimer = window.setTimeout(() => store.set({ awaitingReply: false }), REPLY_TIMEOUT_MS)
    }
  }

  function abort(message: string): false {
    clearReplyTimer()
    const url = s().pending?.previewUrl
    if (url) URL.revokeObjectURL(url)
    store.set({
      pending: null,
      awaitingReply: false,
      error: message,
      phase: s().conversation ? 'chat' : 'welcome',
    })
    return false
  }

  function fail(err: unknown) {
    store.set({ error: messageFor(err) })
  }

  function messageFor(err: unknown): string {
    if (err instanceof IdentityError) return ERR_IDENTITY
    if (err instanceof NetworkError) return ERR_NETWORK
    if (isApiError(err, undefined, 'upload_failed')) return ERR_UPLOAD
    if (isApiError(err, 409, 'conversation_resolved')) return 'This conversation was closed. Start a new one to continue.'
    // Checked before the origin branch below: a quota refusal is also a 403,
    // but it has nothing to do with the site being allowed.
    if (isApiError(err, 403, 'quota_exceeded')) return ERR_QUOTA
    if (isApiError(err) && (err.status === 403 || err.status === 404)) {
      console.warn('[airys widget] the API refused this site or client id:', err.message)
      return ERR_ORIGIN
    }
    return ERR_GENERIC
  }

  function clearReplyTimer() {
    if (replyTimer) {
      window.clearTimeout(replyTimer)
      replyTimer = undefined
    }
  }

  // Page only, no query string or hash: those can carry tokens and PII.
  function sourceUrl(): string {
    return location.origin + location.pathname
  }

  // ── Identity ────────────────────────────────────────────────────────

  // Asks the host for a fresh JWT and waits a little for identify() to land.
  function waitForIdentity(): Promise<string | null> {
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        identityWaiter = null
        resolve(null)
      }, IDENTITY_WAIT_MS)
      identityWaiter = {
        before: deps.getJwt(),
        resolve: (jwt) => {
          window.clearTimeout(timer)
          identityWaiter = null
          resolve(jwt)
        },
      }
      hooks.onIdentityExpired()
    })
  }

  async function identify(jwt: string | null) {
    const { droppedConversation } = await session.rebind(jwt)
    store.set({ verified: session.isVerified() })
    if (!jwt) {
      startNew()
      return
    }
    // The anonymous thread belongs to an anonymous contact and cannot follow
    // them in. Say so, rather than leave it on screen to vanish mid-send.
    if (droppedConversation) lostConversation(NOTICE_SIGNED_IN)
    if (identityWaiter && jwt !== identityWaiter.before) identityWaiter.resolve(jwt)
    // The stream carries the old token in its URL; reopen it with the new one.
    if (sse) {
      disconnect()
      connect()
    }
  }

  // ── Live updates ────────────────────────────────────────────────────

  function connect() {
    if (deps.preview || destroyed) return
    const conversation = s().conversation
    const sess = readSession()
    if (!conversation || !sess) return
    if (!sse && conversation.status === 'active') {
      try {
        sse = new EventSource(api.streamUrl(sess.token, conversation.id))
      } catch {
        sse = null
      }
      // Frames are only a wake-up call; the payload is never trusted.
      if (sse) sse.onmessage = () => void refresh()
    }
    watch()
  }

  // The poll runs for as long as the panel is open and a conversation exists,
  // whatever its status.
  function watch() {
    if (deps.preview || destroyed || fallbackTimer) return
    // Status changes (resolve, takeover, reopen) never reach the stream, so a
    // poll catches them — and a resolve is what puts the CSAT card on screen,
    // so half a minute of waiting reads as nothing happening. Four calls a
    // minute stays well inside the API's 60/min per IP.
    fallbackTimer = window.setInterval(() => void refresh(), FALLBACK_MS)
  }

  function closeStream() {
    if (sse) {
      sse.close()
      sse = null
    }
  }

  function disconnect() {
    closeStream()
    if (fallbackTimer) {
      window.clearInterval(fallbackTimer)
      fallbackTimer = undefined
    }
  }

  // After the tab was hidden or offline the stream may be dead and the
  // token may have rolled: refetch, then rebuild the stream.
  async function reconnect() {
    if (deps.preview || destroyed || !s().conversation) return
    // A long idle may have slid the session out; renew it before asking anything.
    await session.refresh()
    await refresh()
    disconnect()
    connect()
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') void reconnect()
  }
  const onOnline = () => void reconnect()
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', onOnline)

  // ── Other actions ───────────────────────────────────────────────────

  function startNew() {
    disconnect()
    clearReplyTimer()
    clearConversationId()
    store.set({
      conversation: null,
      messages: [],
      pending: null,
      awaitingReply: false,
      csatSubmitted: false,
      notice: null,
      error: null,
      historyDetail: null,
      phase: 'welcome',
    })
  }

  async function submitCsat(satisfied: boolean, comment?: string) {
    const id = s().conversation?.id
    if (!id || deps.preview) return
    try {
      const out = await session.authed((t) => api.submitCsat(t, id, satisfied, comment))
      if (!out.ok) {
        lostConversation()
        return
      }
      store.set({ csatSubmitted: true, conversation: out.value })
    } catch (err) {
      fail(err)
    }
  }

  async function openHistory() {
    store.set({ phase: 'history-list', history: null, historyDetail: null })
    try {
      const out = await session.authed((t) => api.listHistory(t), { keepOnUnauthorized: true })
      store.set({ history: out.ok ? out.value : [] })
    } catch (err) {
      store.set({ history: [] })
      fail(err)
    }
  }

  async function openHistoryDetail(id: string) {
    try {
      const out = await session.authed((t) => api.getHistoryConversation(t, id), {
        keepOnUnauthorized: true,
      })
      if (out.ok) store.set({ historyDetail: out.value, phase: 'history-detail' })
    } catch (err) {
      fail(err)
    }
  }

  function backToChat() {
    store.set({ phase: s().conversation ? 'chat' : 'welcome', historyDetail: null })
  }

  function onOpen() {
    if (!s().conversation) return
    void session.refreshIfStale().then(refresh)
    connect()
  }

  // Nobody is looking: stop the stream and the poll until the panel is back.
  function onClose() {
    disconnect()
  }

  function destroy() {
    destroyed = true
    disconnect()
    clearReplyTimer()
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', onOnline)
  }

  // ── Preview (admin Live Preview iframe): no network, no storage ─────

  function previewSend(text: string): boolean {
    const now = new Date().toISOString()
    const message = (role: Message['role'], content: string): Message => ({
      id: `preview-${++previewCounter}`,
      conversationId: 'preview',
      role,
      content,
      confidence: null,
      createdAt: now,
    })
    const conversation: Conversation = s().conversation ?? {
      id: 'preview',
      status: 'active',
      takeoverActive: false,
      startedAt: now,
      lastMessageAt: now,
      messageCount: 0,
      lastMessagePreview: null,
    }
    store.set((st) => ({
      conversation,
      messages: [...st.messages, message('user', text)],
      pending: null,
      awaitingReply: true,
      phase: 'chat',
    }))
    window.setTimeout(() => {
      if (destroyed) return
      store.set((st) => ({
        messages: [...st.messages, message('assistant', "Thanks for your message! This is a preview, so I'm just echoing back.")],
        awaitingReply: false,
      }))
    }, 600)
    return true
  }

  return {
    hydrate,
    send,
    refresh,
    startNew,
    submitCsat,
    openHistory,
    openHistoryDetail,
    backToChat,
    identify,
    onOpen,
    onClose,
    destroy,
  }
}
